import WolfSearch from '@components/ui/WolfSearch';
import WolfStarRate from '@components/ui/WolfStarRate';
import WolfTooltip from '@components/ui/WolfTooltip';
import { graphQlClient } from '@config/graphqlClient';
import FeedbackGraphQL from '@graphql/feedback.queries';
import { useLoader } from '@hooks/useLoader';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Feedback, getPaginatedFeedbackRequest } from '@models/feedback.model';
import { Email } from '@mui/icons-material';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { useAppDispatch } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { useCallback, useEffect, useState } from 'react';

const FeedbackPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { setLoading } = useLoader();
  const localeFeedback = useTranslation('feedback');
  const localeCommon = useTranslation('common');

  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search term
  useEffect(() => {
    // Skip debounce on initial mount (when both are empty)
    if (searchTerm === '' && debouncedSearch === '') {
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page when search changes (0-based index)
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const data: getPaginatedFeedbackRequest = await graphQlClient.request(
        FeedbackGraphQL.queries.getPaginatedFeedback,
        {
          criteria: {
            page,
            limit: rowsPerPage,
            search: debouncedSearch || undefined,
            orderBy: 'createdAt',
            orderDirection: 'DESC',
          },
        }
      );
      setFeedbackList(data?.getPaginatedFeedback?.feedbacks || []);
      setTotalCount(data?.getPaginatedFeedback?.count || 0);
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, localeCommon, setLoading, page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const getFeedbackTypeLabel = (type: string) => {
    switch (type) {
      case 'BUG':
        return localeFeedback['typeBug'];
      case 'FEATURE_REQUEST':
        return localeFeedback['typeFeatureRequest'];
      case 'IMPROVEMENT':
        return localeFeedback['typeImprovement'];
      case 'OTHER':
        return localeFeedback['typeOther'];
      default:
        return type;
    }
  };

  const getFeedbackTypeColor = (type: string): 'error' | 'primary' | 'success' | 'default' => {
    switch (type) {
      case 'BUG':
        return 'error';
      case 'FEATURE_REQUEST':
        return 'primary';
      case 'IMPROVEMENT':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box className="max-w-screen-2xl mx-auto">
      <h1>{localeFeedback['pageTitle']}</h1>
      <Box className="p-4">
        {/* Search Input */}
        <Box className="mb-6">
          <WolfSearch
            placeholder={localeCommon['search']}
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          />
        </Box>

        {feedbackList.length === 0 ? (
          <Box className="text-center py-12">
            <Typography variant="body14" color="textSecondary">
              {localeFeedback['noFeedback']}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="body14semibold">{localeFeedback['tableUser']}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body14semibold">{localeFeedback['tableType']}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body14semibold">{localeFeedback['tableRating']}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body14semibold">{localeFeedback['tableComment']}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body14semibold">{localeFeedback['tableDate']}</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {feedbackList.map((feedback) => (
                  <TableRow key={feedback.feedbackId} hover>
                    <TableCell>
                      <Box className="flex justify-center items-center gap-2">
                        <Typography variant="body14">
                          {feedback.user?.firstName && feedback.user?.lastName
                            ? `${feedback.user.firstName} ${feedback.user.lastName}`
                            : feedback.user?.email || '-'}
                        </Typography>
                        <WolfTooltip title={feedback.user?.email}>
                          <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </WolfTooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getFeedbackTypeLabel(feedback.type)}
                        color={getFeedbackTypeColor(feedback.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {feedback.rating ? <WolfStarRate rating={feedback.rating} /> : <Typography>-</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body14" sx={{ maxWidth: 400 }}>
                        {feedback.comment || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body12">{formatDate(feedback.createdAt)}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Box>
    </Box>
  );
};

export default FeedbackPage;
